// 桌面拖拽业务动作(spec 260508-folder-feature §5.5 / plan §5.2-§5.5)。
//
// 这里集中所有"拖拽落地后修改 layout"的 helper:
//   - mergeAppsToFolder   App 拖到 App → 创建文件夹
//   - joinFolder          App 拖到 Folder → 加入末尾
//   - moveOutOfFolder     弹层内 App 拖到桌面空白 → 移出 + 自动解散空 folder
//   - reorderApp          桌面 App 拖到桌面空白格 → 排序
//   - reorderFolder       桌面 Folder 拖到桌面空白格 → 排序
//
// 返回值统一通过 ``applyLocalChange`` 的 mutator 形式落库;helper 只负责 mutate。
// 这样 dispatcher 在一处看清"哪条规则触发了哪个 mutator",测试也好写。

import type { DesktopFolderDTO, DesktopItemDTO, DesktopLayoutDTO } from '@blade/os-sdk';
import { generateFolderId } from '@blade/os-sdk';

import {
  findDesktopAppIndex,
  findDesktopFolderIndex,
  findFolderById,
  nextFolderName,
  reorderDesktopItems,
} from './desktopDnD';

// ---------------------------------------------------------------------------
// merge: 桌面 App A 拖到桌面 App B → 在 B 的占位创建新文件夹 [B, A]
// ---------------------------------------------------------------------------

/**
 * 严格 4 步顺序(plan §5.2):
 *   1. 找 ``t = items.indexOf(targetApp)``
 *   2. ``items[t] = newFolderItem``
 *   3. 从 items 中移除 sourceApp
 *   4. ``folders.push({ id, name, items: [targetAppId, sourceAppId] })``
 *
 * 视觉表现:
 *   - source 在 target 之后 → folder 落在 target 原 idx
 *   - source 在 target 之前 → folder 实际下标变成 t-1(后续元素整体前移)
 *
 * 返回新生成的 folder id,供调用方自动打开弹层。
 */
export function mergeAppsToFolder(
  draft: DesktopLayoutDTO,
  targetAppId: string,
  sourceAppId: string,
  baseName: string,
): string {
  if (targetAppId === sourceAppId) return ''; // 自己拖到自己,不应触发
  const t = findDesktopAppIndex(draft, targetAppId);
  const s = findDesktopAppIndex(draft, sourceAppId);
  if (t < 0 || s < 0) return '';

  const folderId = generateFolderId();
  const folderName = nextFolderName(draft, baseName);

  const folderItem: DesktopItemDTO = {
    type: 'folder',
    folderId,
    name: folderName,
  };
  // 步骤 2: 替换 t 位置为 folder
  draft.items[t] = folderItem;
  // 步骤 3: 删除 source(注意:删除可能影响 t 之后的下标,但我们已经写完 t 了,
  // 这里只关心从 items 中移除 sourceApp。)
  const sIdx = draft.items.findIndex(
    (it) => it.type === 'app' && it.appId === sourceAppId,
  );
  if (sIdx >= 0) {
    draft.items.splice(sIdx, 1);
  }
  // 步骤 4: 追加 folder
  const folder: DesktopFolderDTO = {
    id: folderId,
    name: folderName,
    items: [targetAppId, sourceAppId],
  };
  draft.folders.push(folder);

  return folderId;
}

// ---------------------------------------------------------------------------
// create: 右键单个桌面 App →「移入新建文件夹」
// ---------------------------------------------------------------------------

/**
 * 在 ``appId`` 的原位创建一个只含它的文件夹,返回新 folderId(失败返回 '')。
 *
 * 为什么只放一个:后端 ``reconcile_layout`` 有「删空 folder」一步,空文件夹存不住,
 * 所以「新建文件夹」必须至少带一个成员。用户建完再往里拖第二个即可。
 *
 * 注意单成员文件夹是**合法中间态**:``moveOutOfFolder`` 里"剩 ≤1 就解散"说的是
 * 拖出场景(plan §5.7),不影响这里显式创建。
 */
export function createFolderWithApp(
  draft: DesktopLayoutDTO,
  appId: string,
  baseName: string,
): string {
  const idx = findDesktopAppIndex(draft, appId);
  if (idx < 0) return '';

  const folderId = generateFolderId();
  const folderName = nextFolderName(draft, baseName);

  // 原位替换成 folder item —— 保持用户对该图标位置的记忆。
  draft.items[idx] = { type: 'folder', folderId, name: folderName };
  draft.folders.push({ id: folderId, name: folderName, items: [appId] });

  return folderId;
}

// ---------------------------------------------------------------------------
// join: 桌面 App 拖到桌面 Folder → folder.items 末尾追加,桌面 items 删除该 App
// ---------------------------------------------------------------------------

export function joinFolder(
  draft: DesktopLayoutDTO,
  folderId: string,
  sourceAppId: string,
): boolean {
  const folder = findFolderById(draft, folderId);
  if (!folder) return false;
  // 已经在该 folder 里 → 不重复
  if (folder.items.includes(sourceAppId)) return false;

  const sIdx = findDesktopAppIndex(draft, sourceAppId);
  if (sIdx < 0) return false;

  draft.items.splice(sIdx, 1);
  folder.items.push(sourceAppId);
  return true;
}

// ---------------------------------------------------------------------------
// moveOutOfFolder: 弹层内 App 拖到桌面空白 → 移出
// ---------------------------------------------------------------------------

/**
 * 返回 'dissolved' 表示该 folder 在移出后变空,被自动解散(调用方应关闭弹层)。
 * 返回 'kept' 表示 folder 仍存在(items 仅减少一个)。
 * 返回 'noop' 表示参数不合法或 folder/app 不存在。
 */
export function moveOutOfFolder(
  draft: DesktopLayoutDTO,
  folderId: string,
  appId: string,
  insertIndex: number,
): 'dissolved' | 'kept' | 'noop' {
  const folder = findFolderById(draft, folderId);
  if (!folder) return 'noop';
  const memberIdx = folder.items.indexOf(appId);
  if (memberIdx < 0) return 'noop';

  folder.items.splice(memberIdx, 1);
  // 在 layout.items 中按 insertIndex 插入(clamp 到 [0, items.length])。
  const t = Math.max(0, Math.min(draft.items.length, insertIndex));
  draft.items.splice(t, 0, { type: 'app', appId });

  if (folder.items.length <= 1) {
    // 剩余 0 或 1 个 App 时解散:单 App 文件夹没有意义，把残余成员释放回桌面。
    const fIdx = findDesktopFolderIndex(draft, folderId);
    for (const remainingAppId of folder.items) {
      const insertAt = fIdx >= 0 ? fIdx : draft.items.length;
      draft.items.splice(insertAt, 0, { type: 'app', appId: remainingAppId });
    }
    // 删 desktop folder item 与 folders[]。
    const fIdx2 = findDesktopFolderIndex(draft, folderId);
    if (fIdx2 >= 0) draft.items.splice(fIdx2, 1);
    const folderArrIdx = draft.folders.findIndex((f) => f.id === folderId);
    if (folderArrIdx >= 0) draft.folders.splice(folderArrIdx, 1);
    return 'dissolved';
  }
  return 'kept';
}

// ---------------------------------------------------------------------------
// reorder: 桌面 item 拖到桌面空白格
// ---------------------------------------------------------------------------

export function reorderApp(
  draft: DesktopLayoutDTO,
  appId: string,
  target: number,
): void {
  const fromIdx = findDesktopAppIndex(draft, appId);
  if (fromIdx < 0) return;
  draft.items = reorderDesktopItems(draft.items, fromIdx, target);
}

export function reorderFolder(
  draft: DesktopLayoutDTO,
  folderId: string,
  target: number,
): void {
  const fromIdx = findDesktopFolderIndex(draft, folderId);
  if (fromIdx < 0) return;
  draft.items = reorderDesktopItems(draft.items, fromIdx, target);
}
